import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import genieImg from '../assets/appee-genie.png';
import logoImg  from '../assets/bee-appee-logo.png';
import './Auth.css';

const SLIDES = [
  "80% des clients recommandent une entreprise qui leur envoie des vœux personnalisés.",
  "+481% de taux d'ouverture sur les e-mails d'anniversaire par rapport aux e-mails classiques.",
  "Les clients qui reçoivent des vœux d'anniversaire dépensent 25% de plus en moyenne.",
  "Un simple message d'anniversaire peut transformer un client en ambassadeur de marque.",
  "92% des clients se sentent valorisés quand une entreprise se souvient de leur anniversaire.",
];

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 96 96" fill="none">
    <rect x="16" y="24" width="64" height="48" rx="14" stroke="currentColor" strokeWidth="6"/>
    <path d="M22 34l26 22 26-22" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 96 96" fill="none">
    <rect x="22" y="42" width="52" height="38" rx="12" stroke="currentColor" strokeWidth="6"/>
    <path d="M34 42v-9c0-10 6-17 14-17s14 7 14 17v9" stroke="currentColor" strokeWidth="6" strokeLinecap="round"/>
    <circle cx="48" cy="61" r="5" fill="currentColor"/>
  </svg>
);
const IconEye = ({ off }) => off ? (
  <svg width="18" height="18" viewBox="0 0 96 96" fill="none">
    <path d="M12 48s13-22 36-22 36 22 36 22-13 22-36 22-36-22-36-22Z" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="48" cy="48" r="11" stroke="currentColor" strokeWidth="6"/>
    <line x1="16" y1="16" x2="80" y2="80" stroke="currentColor" strokeWidth="6" strokeLinecap="round"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 96 96" fill="none">
    <path d="M12 48s13-22 36-22 36 22 36 22-13 22-36 22-36-22-36-22Z" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="48" cy="48" r="11" stroke="currentColor" strokeWidth="6"/>
  </svg>
);

const IconChevron = ({ dir }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <polyline
      points={dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'}
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { login, loginDemo, sessionExpired } = useApp();

  const [form, setForm]         = useState({ email: '', password: '' });
  const [showPwd, setShowPwd]   = useState(false);
  const [remember, setRemember]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [slideIdx, setSlideIdx]     = useState(0);
  const [animKey, setAnimKey]       = useState(0);
  const [showForgot, setShowForgot] = useState(false);

  /* ── Auto-advance carousel ── */
  useEffect(() => {
    const id = setInterval(() => {
      setSlideIdx(i => (i + 1) % SLIDES.length);
      setAnimKey(k => k + 1);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const goTo = (i) => { setSlideIdx(i); setAnimKey(k => k + 1); };
  const prev = () => goTo((slideIdx - 1 + SLIDES.length) % SLIDES.length);
  const next = () => goTo((slideIdx + 1) % SLIDES.length);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Identifiants incorrects. Vérifiez votre e-mail et mot de passe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="lp-page">

      {/* ══════════ LEFT PANEL ══════════ */}
      <section className="lp-left">

        {/* Dot grids */}
        <div className="lp-dots lp-dots-tl" />
        <div className="lp-dots lp-dots-br" />

        {/* White organic blob */}
        <div className="lp-blob" />

        {/* Génie — large, center-right */}
        <img src={genieImg} alt="Génie Appee" className="lp-genie" />

        {/* Text — mid-left */}
        <div className="lp-left-text">
          <h1 className="lp-headline">
            <span className="lp-headline-light">Le <strong>génie</strong> de tes</span><br/>
            <span className="lp-headline-accent">RELATIONS</span>
          </h1>
          <div className="lp-headline-underline" />
          <p className="lp-tagline">
            Je pense à tes clients avant toi !
          </p>
        </div>

        {/* Quote carousel */}
        <div className="lp-quote">
          <button className="lp-quote-arrow" onClick={prev} aria-label="Précédent">
            <IconChevron dir="left" />
          </button>

          <div className="lp-quote-body">
            <div className="lp-quote-icon">
              <svg width="22" height="18" viewBox="0 0 28 22" fill="none">
                <path d="M0 22V13.2C0 9.73 .9 6.8 2.7 4.4 4.5 2 7.1.6 10.5 0L12 3.6C10.1 4.13 8.53 5.03 7.3 6.3 6.07 7.57 5.37 9.13 5.2 11H10V22H0ZM16 22V13.2C16 9.73 16.9 6.8 18.7 4.4 20.5 2 23.1.6 26.5 0L28 3.6C26.1 4.13 24.53 5.03 23.3 6.3 22.07 7.57 21.37 9.13 21.2 11H26V22H16Z" fill="#FF9800"/>
              </svg>
            </div>
            <p key={animKey} className="lp-quote-text">{SLIDES[slideIdx]}</p>
            <div className="lp-quote-dots">
              {SLIDES.map((_, i) => (
                <button key={i} className={`lp-dot${i === slideIdx ? ' lp-dot-on' : ''}`} onClick={() => goTo(i)} />
              ))}
            </div>
          </div>

          <button className="lp-quote-arrow" onClick={next} aria-label="Suivant">
            <IconChevron dir="right" />
          </button>
        </div>

      </section>

      {/* ══════════ RIGHT PANEL ══════════ */}
      <section className="lp-right">
        <div className="lp-form-wrap">

          <img src={logoImg} alt="bee appee" className="lp-logo" />

          <h2 className="lp-card-title">Connexion 👋</h2>
          <p className="lp-card-sub">
            Bienvenue ! Accédez à votre tableau de bord<br/>et pilotez vos relations en toute simplicité.
          </p>

          {sessionExpired && (
            <div className="lp-banner lp-banner-warn">
              ⏱ Session expirée après 10 min d'inactivité. Veuillez vous reconnecter.
            </div>
          )}
          {error && (
            <div className="lp-banner lp-banner-error">⚠️ {error}</div>
          )}

          <form onSubmit={handleSubmit} noValidate>

            <div className="lp-field-group">
              <label className="lp-label">E-mail</label>
              <div className="lp-field">
                <span className="lp-field-icon"><IconMail /></span>
                <input className="lp-input" type="email" placeholder="votre@email.com"
                  value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  required autoComplete="email" />
              </div>
            </div>

            <div className="lp-field-group">
              <label className="lp-label">Mot de passe</label>
              <div className="lp-field">
                <span className="lp-field-icon"><IconLock /></span>
                <input className="lp-input" type={showPwd ? 'text' : 'password'} placeholder="••••••••"
                  value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
                  required autoComplete="current-password" />
                <button type="button" className="lp-eye-btn" onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
                  <IconEye off={showPwd} />
                </button>
              </div>
            </div>

            <div className="lp-row">
              <label className="lp-remember">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="lp-checkbox" />
                <span>Se souvenir de moi</span>
              </label>
              <button type="button" className="lp-forgot" onClick={() => setShowForgot(true)}>Mot de passe oublié ?</button>
            </div>

            <button type="submit" className={`lp-btn${loading ? ' lp-btn-loading' : ''}`} disabled={loading}>
              {loading
                ? <span className="lp-btn-spinner" />
                : <>Se connecter <span className="lp-btn-arrow">→</span></>
              }
            </button>

          </form>

          <div className="lp-separator"><span>ou</span></div>

          <p className="lp-signup">
            Pas encore de compte ?{' '}
            <Link to="/register" className="lp-link">Créer un compte gratuit →</Link>
          </p>

        </div>
      </section>

    </div>

    {/* ══════════ MODAL MOT DE PASSE OUBLIÉ ══════════ */}
    {showForgot && (
      <ForgotPasswordModal onClose={() => setShowForgot(false)} />
    )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Composant modal "Mot de passe oublié"
// ─────────────────────────────────────────────────────────────────────────────
function ForgotPasswordModal({ onClose }) {
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fpw-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="fpw-modal">
        <button className="fpw-close" onClick={onClose} aria-label="Fermer">✕</button>

        {sent ? (
          <div className="fpw-sent">
            <div className="fpw-sent-icon">📧</div>
            <h3 className="fpw-title">E-mail envoyé !</h3>
            <p className="fpw-sub">
              Si un compte existe pour <strong>{email}</strong>, vous allez recevoir un lien de réinitialisation dans les prochaines minutes.
            </p>
            <p className="fpw-hint">Vérifiez vos spams si vous ne le voyez pas.</p>
            <button className="fpw-btn" onClick={onClose}>Fermer</button>
          </div>
        ) : (
          <>
            <div className="fpw-icon">🔑</div>
            <h3 className="fpw-title">Mot de passe oublié ?</h3>
            <p className="fpw-sub">
              Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>

            {error && <div className="fpw-error">⚠️ {error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="fpw-field">
                <label className="fpw-label">Adresse e-mail</label>
                <input
                  className="fpw-input"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
              <button
                type="submit"
                className={`fpw-btn${loading ? ' fpw-btn-loading' : ''}`}
                disabled={loading || !email.trim()}
              >
                {loading ? <span className="fpw-spinner" /> : 'Envoyer le lien de réinitialisation'}
              </button>
            </form>

            <button className="fpw-back" onClick={onClose}>← Retour à la connexion</button>
          </>
        )}
      </div>
    </div>
  );
}
